import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Spinner } from "@/components/common";
import { Users, UserPlus, Archive, Activity, Building2, AlertTriangle } from "lucide-react";

function Metric({ icon: Icon, label, value, tone = "vino", testid }) {
  const tones = {
    vino: "bg-vino-50 text-vino-700",
    emerald: "bg-emerald-50 text-emerald-700",
    amber: "bg-amber-50 text-amber-700",
    slate: "bg-slate-100 text-slate-600",
  };
  return (
    <div data-testid={testid} className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className={`mb-3 flex h-10 w-10 items-center justify-center rounded-lg ${tones[tone]}`}>
        <Icon className="h-5 w-5" />
      </div>
      <p className="font-display text-3xl font-bold text-slate-900">{value}</p>
      <p className="mt-1 text-sm text-slate-500">{label}</p>
    </div>
  );
}

export default function Inicio() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/stats").then((r) => setStats(r.data)).catch(() => setStats(false));
  }, []);

  if (stats === null)
    return (
      <div className="flex h-64 items-center justify-center text-vino-700">
        <Spinner className="h-6 w-6" />
      </div>
    );
  if (stats === false) return <p className="text-sm text-slate-500">No fue posible cargar el panel.</p>;

  const maxEmp = Math.max(1, ...stats.por_empresa.map((e) => e.total));

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">Inicio</h1>
        <p className="mt-1 text-sm text-slate-500">Resumen general de empleados y vinculaciones.</p>
      </div>

      {!stats.implementation_date && (
        <div className="flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4" data-testid="alerta-fecha-implementacion">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
          <div className="text-sm text-amber-800">
            <p className="font-semibold">Falta configurar la fecha de implementación de Roge.</p>
            <p className="mt-0.5">
              Es necesaria para la carga inicial.{" "}
              <Link to="/configuracion" className="font-semibold underline">Ir a Configuración</Link>
            </p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Metric testid="metric-total" icon={Users} label="Vinculaciones" value={stats.total_vinculaciones} tone="vino" />
        <Metric testid="metric-roge" icon={UserPlus} label="Ingresados por Roge" value={stats.roge} tone="emerald" />
        <Metric testid="metric-carga" icon={Archive} label="Carga inicial" value={stats.carga_inicial} tone="amber" />
        <Metric testid="metric-activos" icon={Activity} label="Activos / Retirados" value={`${stats.activos} / ${stats.retirados}`} tone="slate" />
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="mb-4 flex items-center gap-2">
          <Building2 className="h-5 w-5 text-vino-700" />
          <h2 className="font-display text-lg font-semibold text-slate-900">Distribución por empresa cliente</h2>
        </div>
        {stats.por_empresa.length === 0 ? (
          <p className="py-8 text-center text-sm text-slate-400">Aún no hay vinculaciones registradas.</p>
        ) : (
          <div className="space-y-3">
            {stats.por_empresa.map((e) => (
              <div key={e.empresa} className="flex items-center gap-3">
                <span className="w-40 shrink-0 truncate text-sm text-slate-600">{e.empresa}</span>
                <div className="h-2.5 flex-1 overflow-hidden rounded-full bg-slate-100">
                  <div className="h-full rounded-full bg-vino-600" style={{ width: `${(e.total / maxEmp) * 100}%` }} />
                </div>
                <span className="w-8 shrink-0 text-right text-sm font-semibold text-slate-700">{e.total}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
